import type { PlaneKey } from './joints'
import type { PlaneLimit } from './model'
import type { Side } from './gait'

/**
 * Kanäle — Ableitungen, Glättung, Grenzwertprüfung.
 *
 * Aus einer Winkelkurve über den Zyklus werden Winkelgeschwindigkeit und
 * Winkelbeschleunigung. Das klingt nach zwei Zeilen und ist der heikelste
 * Rechenschritt der ganzen Kette:
 *
 * Zweimal numerisch ableiten verstärkt jede Unstetigkeit. Ein Knick in der
 * Winkelkurve, den niemand sieht, wird in der zweiten Ableitung zu einem
 * Ausschlag, der die inverse Dynamik dominiert — und damit die Momente, aus
 * denen später der Motor ausgelegt wird. Deshalb wird zyklisch abgeleitet
 * (zentrale Differenz über den Zyklusrand hinweg) und die Beschleunigung
 * anschliessend geglättet.
 *
 * Die Glättung ist kein Schönheitsmittel. Sie ist der Preis dafür, dass das
 * Kontaktmodell am Boden mit einem starren Umschalten arbeitet.
 */

/** Die neun Grössenarten, die je Achse anfallen. */
export const QUANTITIES = {
  A: { label: 'Winkel', unit: '°', decimals: 1, band: true },
  V: { label: 'Winkelgeschw.', unit: '°/s', decimals: 0, band: false },
  C: { label: 'Winkelbeschl.', unit: '°/s²', decimals: 0, band: false },
  M: { label: 'Moment', unit: 'Nm', decimals: 1, band: false },
  P: { label: 'Leistung', unit: 'W', decimals: 0, band: false },
  N: { label: 'Antriebsleistung', unit: 'W', decimals: 0, band: false },
  E: { label: 'Energie', unit: 'J', decimals: 1, band: false },
  G: { label: 'Bodenreaktion', unit: 'N', decimals: 0, band: false },
  B: { label: 'Ganzkörper', unit: '', decimals: 2, band: false },
} as const

export type QuantityKey = keyof typeof QUANTITIES

/** Ein Kanalschlüssel wird ERZEUGT, nicht aufgelistet. */
export const channelKey = (
  quantity: QuantityKey,
  joint: string,
  plane: PlaneKey,
  side: Side,
): string => `${quantity}|${joint}|${plane}|${side}`

export interface ChannelParts {
  quantity: QuantityKey
  joint: string
  plane: PlaneKey
  side: Side
}

export function parseChannel(key: string): ChannelParts | null {
  const [quantity, joint, plane, side] = key.split('|')
  if (!quantity || !joint || !plane || !side) return null
  if (!(quantity in QUANTITIES)) return null
  return {
    quantity: quantity as QuantityKey,
    joint,
    plane: plane as PlaneKey,
    side: side as Side,
  }
}

/* -------------------------------------------------------------------------
   Glättung und Ableitung
   ------------------------------------------------------------------------- */

/**
 * Zyklische [1,2,1]-Glättung, `passes` Durchgänge.
 *
 * Zyklisch heisst: der letzte Wert ist Nachbar des ersten. Eine Glättung, die
 * am Zyklusrand aufhört, erzeugt dort genau die Unstetigkeit, die sie
 * beseitigen soll.
 *
 * Arbeitet auf den ersten `count` Werten und lässt den Schlusswert unberührt —
 * er wird von `closeCycle` nachgezogen.
 */
export function smooth(values: Float32Array, count: number, passes: number): void {
  const buffer = new Float32Array(count)
  for (let p = 0; p < passes; p++) {
    for (let i = 0; i < count; i++) buffer[i] = values[i]
    for (let i = 0; i < count; i++) {
      const a = buffer[(i - 1 + count) % count]
      const b = buffer[i]
      const c = buffer[(i + 1) % count]
      values[i] = 0.25 * a + 0.5 * b + 0.25 * c
    }
  }
}

/** Schliesst den Zyklus: der Wert bei 100 % ist der bei 0 %. */
export const closeCycle = (values: Float32Array, count: number): void => {
  values[count] = values[0]
}

/**
 * Zentrale Differenz über den Zyklusrand hinweg.
 *
 * `dt` ist der Abstand ZWEIER Stützstellen, nicht einer — die zentrale
 * Differenz greift auf beide Nachbarn zu. Wer hier den halben Abstand einsetzt,
 * bekommt doppelte Geschwindigkeiten und viermal zu grosse Beschleunigungen,
 * und die Momente sehen trotzdem plausibel aus.
 */
export function derive(values: Float32Array, count: number, dt: number): Float32Array {
  const out = new Float32Array(count + 1)
  const dt2 = 2 * dt
  for (let i = 0; i < count; i++) {
    out[i] = (values[(i + 1) % count] - values[(i - 1 + count) % count]) / dt2
  }
  closeCycle(out, count)
  return out
}

export interface Kinematics {
  /** Winkel, Grad. */
  angle: Float32Array
  /** Winkelgeschwindigkeit, Grad/s. */
  velocity: Float32Array
  /** Winkelbeschleunigung, Grad/s². */
  acceleration: Float32Array
}

/**
 * Winkel → Geschwindigkeit → Beschleunigung.
 *
 * Die Beschleunigung wird dreimal geglättet, die Geschwindigkeit nicht. Das ist
 * aus der Vorlage übernommen und hat einen Grund: die zweite Ableitung ist die
 * erste Grösse, in der das starre Kontaktmodell am Boden als Ausschlag sichtbar
 * wird. Die Geschwindigkeit zu glätten würde dagegen echte Bewegung dämpfen.
 */
export function differentiate(
  angle: Float32Array,
  count: number,
  cycleTime: number,
  accelerationPasses = 3,
): Kinematics {
  const dt = cycleTime / count
  const velocity = derive(angle, count, dt)
  const acceleration = derive(velocity, count, dt)
  smooth(acceleration, count, accelerationPasses)
  closeCycle(acceleration, count)

  const closed = new Float32Array(angle.length)
  closed.set(angle)
  closeCycle(closed, count)

  return { angle: closed, velocity, acceleration }
}

/* -------------------------------------------------------------------------
   Kennwerte
   ------------------------------------------------------------------------- */

export interface Stats {
  min: number
  max: number
  /** Grösster Betrag — der Spitzenwert. */
  peak: number
  /** Arithmetisches Mittel. */
  mean: number
  /**
   * Quadratischer Mittelwert über den vollen Zyklus.
   *
   * FÜR DIE ANTRIEBSAUSLEGUNG DER WICHTIGERE WERT. Die Spitze entscheidet, ob
   * der Antrieb die Bewegung überhaupt schafft; der Effektivwert entscheidet,
   * ob er dabei warm wird — die Verlustleistung geht mit dem Quadrat des
   * Moments. Ein Antrieb, der nach der Spitze ausgewählt wird, ist im
   * Dauerbetrieb entweder überdimensioniert oder er brennt.
   */
  rms: number
}

/**
 * Kennwerte über den vollen Zyklus.
 *
 * Gerechnet über die ersten `count` Werte — der Schlusswert ist die Kopie des
 * ersten und würde ihn sonst doppelt zählen.
 */
export function stats(values: Float32Array, count: number): Stats {
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let square = 0

  for (let i = 0; i < count; i++) {
    const v = values[i]
    if (v < min) min = v
    if (v > max) max = v
    sum += v
    square += v * v
  }

  return {
    min,
    max,
    peak: Math.max(Math.abs(min), Math.abs(max)),
    mean: sum / count,
    rms: Math.sqrt(square / count),
  }
}

/* -------------------------------------------------------------------------
   Grenzwertprüfung
   ------------------------------------------------------------------------- */

export interface Breach {
  /** Anteil des Zyklus ausserhalb der Grenze, 0 … 1. */
  fraction: number
  /** Grösste Überschreitung, in der Einheit der Grösse. */
  worst: number
  /** Bei welcher Phase die grösste Überschreitung liegt, 0 … 1. */
  atPhase: number
}

/**
 * Prüft eine Winkelkurve gegen den Bewegungsbereich.
 *
 * Gibt `null` zurück, wenn nichts überschritten wird — so lässt sich die Frage
 * „gibt es hier ein Problem?" mit einer Prüfung auf `null` beantworten, statt
 * mit dem Vergleich einer Zahl gegen null.
 */
export function checkRange(
  angle: Float32Array,
  count: number,
  limit: PlaneLimit,
): Breach | null {
  let outside = 0
  let worst = 0
  let atPhase = 0

  for (let i = 0; i < count; i++) {
    const v = angle[i]
    const over = v > limit.hi ? v - limit.hi : v < limit.lo ? limit.lo - v : 0
    if (over > 0) {
      outside++
      if (over > worst) {
        worst = over
        atPhase = i / count
      }
    }
  }

  return outside === 0 ? null : { fraction: outside / count, worst, atPhase }
}

/** Prüft eine Momentenkurve gegen das Grenzmoment — beidseitig. */
export function checkMoment(
  moment: Float32Array,
  count: number,
  limit: PlaneLimit,
): Breach | null {
  let outside = 0
  let worst = 0
  let atPhase = 0

  for (let i = 0; i < count; i++) {
    const over = Math.abs(moment[i]) - limit.moment
    if (over > 0) {
      outside++
      if (over > worst) {
        worst = over
        atPhase = i / count
      }
    }
  }

  return outside === 0 ? null : { fraction: outside / count, worst, atPhase }
}
